import { Slot } from '@radix-ui/react-slot';
import { CSSProperties, forwardRef, ReactNode, Ref } from 'react';
import type { FlexProps } from './flex.component';
import { Flex } from './flex.component';

export interface SafeAreaProps {
	/**
	 * Merges its props onto its immediate child.
	 */
	asChild?: boolean;
	horizontalPadding?: string;
	verticalPadding?: string;
	children?: ReactNode;
	maxWidth?: CSSProperties['maxWidth'];
	'data-testid'?: string;
}

function SafeArea(
	{ 'data-testid': dataTestId, asChild, horizontalPadding, verticalPadding, maxWidth, ...props }: SafeAreaProps,
	ref: Ref<HTMLDivElement>,
): JSX.Element {
	const Comp = asChild ? Slot : Flex;

	const { style: unknownStyle, ...rest } = props as unknown as FlexProps<'div'>;
	const style = unknownStyle ?? {};

	return (
		<Comp
			style={{
				alignItems: rest.align ?? 'flex-start',
				width: '100%',
				padding: `${verticalPadding ?? 0} ${horizontalPadding ?? '16px'}`,
				maxWidth: maxWidth ?? '1320px', // TODO: Use Cellula breakpoint.
				marginTop: '0',
				marginRight: 'auto',
				marginBottom: '0',
				marginLeft: 'auto',
				...style,
			}}
			data-testid={dataTestId}
			ref={ref}
			{...rest}
		/>
	);
}

/**
 * To keep contents within a "safe area" (i.e. a fixed padding from the sides),
 * the `<SafeArea />` should be used.
 * It supports Cellula tokens to ensure consistency with other Cellula components.
 */
const _SafeArea = forwardRef(SafeArea);

export { _SafeArea as SafeArea };

SafeArea.displayName = 'CellulaSafeArea';
